import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from './init'

const functions = getFunctions(app, 'northamerica-northeast1')
const CF_BASE = 'https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net'

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options)
  const json = await res.json()
  // Normalize to { data: ... } shape like httpsCallable
  return { data: json?.data ?? json }
}

// Call Analysis Functions
export const callAnalysisFunctions = {
  // Utility: passive ingestion endpoint URL for Snowflake
  getProcessEndpointUrl() {
    return `${CF_BASE}/processCallData`
  },
  // Utility: manual reconciliation endpoint URL
  getManualImportUrl() {
    return `${CF_BASE}/manual_callanalysis_import`
  },
  // Status and monitoring
  async getStatusSummary() {
    const res = await fetchJson(`${CF_BASE}/getStatusSummary`)
    const data = res && res.data
    // Backend returns { success, data: { summary, stepCounts, totals, ... } }
    // Admin expects flat counts at the top level (e.g., conversion_completed)
    if (data && typeof data === 'object' && data.summary && typeof data.summary === 'object') {
      return { data: data.summary }
    }
    return res
  },

  // Step 1: Import
  async triggerImport() {
    // Use fetch-based format (CORS-friendly plain HTTPS)
    return await fetchJson(`${CF_BASE}/manual_callanalysis_import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  // Step 2: Download
  async triggerDownload() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async resetDownload() {
    const response = await fetch('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/reset_callanalysis_download', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    return await response.json()
  },

  // Step 3: Convert
  async triggerConvert() {
    // manual_callanalysis_convert is an HTTP onRequest function, not a callable
    return await fetchJson(`${CF_BASE}/manual_callanalysis_convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async resetConvert() {
    const response = await fetch('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/reset_callanalysis_convert', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    return await response.json()
  },

  async resetStuckConvert() {
    const response = await fetch('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/reset_stuck_conversions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    return await response.json()
  },

  // Step 4: Transcribe
  async triggerTranscribe() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_transcribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async resetTranscribe() {
    // Step 4-specific reset for stuck transcription_processing records
    return await fetchJson(`${CF_BASE}/reset_stuck_transcriptions_step4`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async resetTranscribeErrors() {
    return await fetchJson(`${CF_BASE}/reset_transcription_errors_step4`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async resetTranscriptionBreakers() {
    return await fetchJson(`${CF_BASE}/reset_transcription_breakers_step4`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async resetStuckTranscribe() {
    const response = await fetch('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/reset_stuck_transcriptions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    return await response.json()
  },

  // Step 5: Conversation
  async triggerConversationStep5() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_conversation_step5`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async testConversationStep5(callId) {
    return await fetchJson(`${CF_BASE}/test_callanalysis_conversation_step5`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callId })
    })
  },

  async resetConversation() {
    const response = await fetch('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/reset_conversation_extracting', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    return await response.json()
  },

  async resetConversationErrors() {
    const response = await fetch('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/reset_conversation_errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    return await response.json()
  },

  // Step 6: Summarize
  async triggerSummarizeStep6() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_summarize_step6`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async testSummarizeStep6(callId) {
    return await fetchJson(`${CF_BASE}/test_callanalysis_summarize_step6`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callId })
    })
  },

  async resetSummarizeErrors() {
    return await fetchJson(`${CF_BASE}/reset_callanalysis_summarize_step6`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  async resetStuckSummarize() {
    return await fetchJson(`${CF_BASE}/reset_stuck_summarize_step6`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  // Step 7: Reasoning fanout
  async triggerReasoningFanout(callId) {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_reasoning_step7`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(callId ? { callId } : {})
    })
  },

  async resetReasoningSubstep(substep, state = 'processing', callIds) {
    // Ensure CORS-friendly request body
    return await fetchJson(`${CF_BASE}/reset_reasoning_substep`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ substep, state, callIds })
    })
  },

  // Step 7 substep manual endpoints
  async runCategorization() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_categorization`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },
  async runThreads() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },
  async runSentiment() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_sentiment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },
  async runSatisfaction() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_satisfaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },
  async runOpportunities() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_opportunities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },
  async runJourneys() {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_journeys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
  },

  // Step 8: Daily aggregation (manual only)
  async triggerDailyAggregation({ date, markComplete = false, backfillDays = 1, startDate, endDate } = {}) {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_daily`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        date
          ? { date, markComplete }
          : (startDate && endDate)
            ? { startDate, endDate, markComplete }
            : { backfillDays }
      )
    })
  },

  // Step 7: Batch-run reasoning fanout across summarized records
  async triggerReasoningBatch(limit = 10, { dryRun = false } = {}) {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_reasoning_batch_step7?limit=${encodeURIComponent(limit)}&dryRun=${encodeURIComponent(dryRun)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit, dryRun })
    })
  },

  // Step 7: Run ready items for a specific substep
  async runReasoningReady(substep, limit = 20) {
    return await fetchJson(`${CF_BASE}/manual_callanalysis_reasoning_run_ready`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ substep, limit })
    })
  },

  // Step 6: Analysis (using existing functions)
  async triggerAnalysis() {
    const triggerAnalysis = httpsCallable(functions, 'manual_callanalysis_categorization')
    return await triggerAnalysis()
  },

  // Step 7: Complete
  async triggerComplete() {
    const triggerComplete = httpsCallable(functions, 'manual_callanalysis_complete')
    return await triggerComplete()
  },

  async resetComplete() {
    const response = await fetch('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/reset_callanalysis_complete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    return await response.json()
  },

  // Testing functions
  async testEnhancedRecord(callId) {
    const testEnhancedRecord = httpsCallable(functions, 'test_enhanced_record')
    return await testEnhancedRecord({ callId })
  },

  async testConversationExtraction(callId) {
    const testConversationExtraction = httpsCallable(functions, 'test_conversation_extraction')
    return await testConversationExtraction({ callId })
  },

  // Debug functions
  async debugEnhancedRecords() {
    const debugEnhancedRecords = httpsCallable(functions, 'debug_enhanced_records')
    return await debugEnhancedRecords()
  },

  async debugConversationErrors(callId) {
    const debugConversationErrors = httpsCallable(functions, 'debug_conversation_errors')
    return await debugConversationErrors({ callId })
  },

  // Individual call management functions
  async getCalls(filters = {}) {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v)
    })
    return await fetchJson(`${CF_BASE}/getCalls?${params.toString()}`)
  },

  async getCallDetails(callId) {
    const params = new URLSearchParams({ callId })
    return await fetchJson(`${CF_BASE}/getCallDetails?${params.toString()}`)
  },

  async resetCallStatus(callId, newStatus = 'queued', newStep = 'import') {
    return await fetchJson(`${CF_BASE}/resetCallStatus`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callId, newStatus, newStep })
    })
  },

  async retryCall(callId) {
    return await fetchJson(`${CF_BASE}/retryCall`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callId })
    })
  },

  async stopCall(callId) {
    return await fetchJson(`${CF_BASE}/stopCall`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callId })
    })
  },

  async revertCall(callId, targetStep) {
    return await fetchJson(`${CF_BASE}/revertCall`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callId, targetStep })
    })
  },

  async bulkCallOperation(callIds, operation, options = {}) {
    return await fetchJson(`${CF_BASE}/bulkCallOperation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callIds, operation, options })
    })
  },

  async getPipelineStats() {
    return await fetchJson(`${CF_BASE}/getPipelineStats`)
  }
}

// Export individual functions for direct use
export const {
  getManualImportUrl,
  getProcessEndpointUrl,
  getStatusSummary,
  triggerImport,
  resetImport,
  triggerDownload,
  resetDownload,
  triggerConvert,
  resetConvert,
  resetStuckConvert,
  triggerTranscribe,
  resetTranscribe,
  resetTranscribeErrors,
  resetTranscriptionBreakers,
  resetStuckTranscribe,
  triggerConversationStep5,
  testConversationStep5,
  resetConversation,
  resetConversationErrors,
  triggerSummarizeStep6,
  testSummarizeStep6,
  resetSummarizeErrors,
  resetStuckSummarize,
  triggerAnalysis,
  resetAnalysis,
  triggerReasoningFanout,
  triggerReasoningBatch,
  runReasoningReady,
  resetReasoningSubstep,
  triggerComplete,
  resetComplete,
  testEnhancedRecord,
  testConversationExtraction,
  debugEnhancedRecords,
  debugConversationErrors
} = callAnalysisFunctions
